import React, { useState, useRef, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  container: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', width: '100%' },
  btn: {
    width: '72px', height: '72px', borderRadius: '50%', border: 'none',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '28px', transition: 'all 0.2s', outline: 'none',
  },
  idle: { backgroundColor: '#0052CC', boxShadow: '0 4px 16px rgba(0,82,204,0.3)' },
  recording: { backgroundColor: '#dc2626', boxShadow: '0 0 0 8px rgba(220,38,38,0.15)', animation: 'pulse 1.5s infinite' },
  processing: { backgroundColor: '#9ca3af', cursor: 'not-allowed' },
  label: { fontSize: '13px', fontWeight: '600', color: '#6b7280' },
  error: { fontSize: '12px', color: '#dc2626', textAlign: 'center', maxWidth: '240px' },
  transcript: {
    width: '100%', maxWidth: '480px', minHeight: '48px',
    backgroundColor: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: '10px', padding: '10px 14px',
    fontSize: '14px', color: '#374151', lineHeight: '1.6',
    textAlign: 'left',
  },
  interimText: { color: '#9ca3af', fontStyle: 'italic' },
  finalText: { color: '#111827' },
};

export default function VoiceInput({ onTranscribed, disabled, language }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');

  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const finalTranscriptRef = useRef('');

  useEffect(() => {
    return () => stopRecording();
  }, []);

  async function getDeepgramKey() {
    const res = await fetch(`${API}/api/practice/deepgram-token`);
    const data = await res.json();
    return data.key;
  }

  async function startRecording() {
    setError('');
    setFinalTranscript('');
    setInterimTranscript('');
    finalTranscriptRef.current = '';

    try {
      // Get Deepgram API key from backend
      const dgKey = await getDeepgramKey();

      // Map language to Deepgram language code
      const langMap = {
        english: 'en', spanish: 'es', french: 'fr', german: 'de',
        italian: 'it', portuguese: 'pt', polish: 'pl', hindi: 'hi',
        japanese: 'ja', mandarin: 'zh', korean: 'ko', arabic: 'ar',
        dutch: 'nl', turkish: 'tr', swedish: 'sv', indonesian: 'id',
        ukrainian: 'uk', greek: 'el', czech: 'cs', finnish: 'fi',
        romanian: 'ro', danish: 'da', norwegian: 'no', hungarian: 'hu',
        vietnamese: 'vi', russian: 'ru',
      };
      const langCode = langMap[(language || 'english').toLowerCase()] || 'en';

      // Open Deepgram WebSocket
      const ws = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova-2&language=${langCode}&punctuate=true&interim_results=true&endpointing=300`,
        ['token', dgKey]
      );

      wsRef.current = ws;

      ws.onopen = async () => {
        // Start microphone
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mimeType =
          MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
          MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';

        const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = e => {
          if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            ws.send(e.data);
          }
        };

        mediaRecorder.start(100); // Send chunks every 100ms
        setIsRecording(true);
      };

      ws.onmessage = e => {
        const data = JSON.parse(e.data);
        const transcript = data.channel?.alternatives?.[0]?.transcript || '';
        const isFinal = data.is_final;

        if (isFinal && transcript) {
          finalTranscriptRef.current += (finalTranscriptRef.current ? ' ' : '') + transcript;
          setFinalTranscript(finalTranscriptRef.current);
          setInterimTranscript('');
        } else if (!isFinal && transcript) {
          setInterimTranscript(transcript);
        }
      };

      ws.onerror = () => {
        setError('Connection error. Please try again.');
        cleanup();
      };

      ws.onclose = () => {
        cleanup();
      };

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone in browser settings.');
      } else {
        setError(`Error: ${err.message}`);
      }
      setIsRecording(false);
    }
  }

  function stopRecording() {
    if (!isRecording) return;

    // Stop media recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // Stop microphone
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }

    // Close WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    setIsRecording(false);
    setIsProcessing(true);

    // Small delay to receive final transcripts
    setTimeout(() => {
      const text = finalTranscriptRef.current.trim();
      if (text) {
        onTranscribed && onTranscribed(text);
      } else {
        setError('No speech detected. Please try again.');
      }
      setIsProcessing(false);
      setFinalTranscript('');
      setInterimTranscript('');
      finalTranscriptRef.current = '';
    }, 600);
  }

  function cleanup() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    setIsRecording(false);
    setIsProcessing(false);
  }

  function handleClick() {
    if (disabled || isProcessing) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }

  const btnStyle = isProcessing
    ? { ...styles.btn, ...styles.processing }
    : isRecording
      ? { ...styles.btn, ...styles.recording }
      : { ...styles.btn, ...styles.idle };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(220,38,38,0.4); }
          70% { box-shadow: 0 0 0 16px rgba(220,38,38,0); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }
      `}</style>

      {/* Real-time transcript display */}
      {(isRecording || finalTranscript || interimTranscript) && (
        <div style={styles.transcript}>
          {finalTranscript && <span style={styles.finalText}>{finalTranscript}</span>}
          {interimTranscript && (
            <span style={styles.interimText}>
              {finalTranscript ? ' ' : ''}{interimTranscript}
            </span>
          )}
          {!finalTranscript && !interimTranscript && isRecording && (
            <span style={styles.interimText}>Listening...</span>
          )}
        </div>
      )}

      <button
        style={btnStyle}
        onClick={handleClick}
        disabled={disabled || isProcessing}
      >
        {isProcessing ? '⏳' : isRecording ? '⏹' : '🎙'}
      </button>

      <div style={styles.label}>
        {isProcessing ? 'Processing...' :
         isRecording ? 'Recording — press to send' :
         'Press to speak'}
      </div>

      {error && <div style={styles.error}>{error}</div>}
    </div>
  );
}
